import React, { useMemo } from 'react';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { ThumbnailProps } from '@swyftx/aviary/atoms/Thumbnail';
import { ThumbnailStack } from '@swyftx/aviary/atoms/ThumbnailStack';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { BodyProps } from '@swyftx/aviary/atoms/Typography/Body/Body.styles';
import { Star } from '@swyftx/aviary/icons/outlined';
import { MarginInstrument as MarginInstrumentType } from '@swyftx/margin/build/types';

import { useMarginInstrumentDetails } from 'src/lib/margin/hooks/instruments';
import { useMarkets } from 'src/lib/markets/hooks/useMarkets';

type Props = {
  market?: MarginInstrumentType;
  showMarketSymbol?: boolean;
  showMarketName?: boolean;
  showFavourite?: boolean;
  size: 'small' | 'medium';
  weight?: BodyProps['weight'];
};

const MarginInstrument: React.FC<Props> = ({
  showFavourite = false,
  market,
  showMarketSymbol = true,
  showMarketName = false,
  weight = 'none',
  size,
}) => {
  const { getAssetsByUuid } = useMarkets();
  const { symbol, name } = useMarginInstrumentDetails({ instrument: market });

  const assets = useMemo(() => {
    if (!market) return [];

    return getAssetsByUuid([market.secondaryAssetUuid, market.primaryAssetUuid]);
  }, [getAssetsByUuid, market]);

  const thumbnailSize = useMemo((): ThumbnailProps['size'] => {
    switch (size) {
      case 'small':
        return 'xsm';
      case 'medium':
        return 'sm';
      default:
        size satisfies never;
        return 'md';
    }
  }, [size]);

  if (!market) return null;

  return (
    <FlexLayout justifyContent='start' alignItems='center' spacing={12} className='ml-8'>
      {showFavourite && <Button variant='ghost' layout='icon' size='sm' leadingIcon={<Star className='h-20 w-20' />} />}
      <ThumbnailStack
        size={thumbnailSize}
        thumbnails={assets.map((asset) => ({
          type: 'asset',
          hasPadding: false,
          appearance: 'circle',
          assetCode: asset?.code,
        }))}
      />
      <FlexLayout justifyContent='start' alignItems='center' spacing={4}>
        {showMarketSymbol && (
          <Body size={size} weight={showMarketName ? 'bold' : weight}>
            {symbol}
          </Body>
        )}
        {showMarketName && (
          <Body size={size} color='secondary' className='max-w-[180px] truncate'>
            {name}
          </Body>
        )}
      </FlexLayout>
    </FlexLayout>
  );
};

export { MarginInstrument };
